import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthProvider';
import { teamManagerService } from '../services/teamManagerService';

const TeamLogin = () => {
    const navigate = useNavigate();
    const { user, loading } = useAuth();

    const [email, setEmail] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    // If already authenticated, jump straight to the roster.
    useEffect(() => {
        if (!loading && user) {
            navigate('/team/roster', { replace: true });
        }
    }, [loading, user, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        if (!email.trim()) return;

        setSubmitting(true);
        try {
            const redirectTo = `${window.location.origin}/team/roster`;
            await teamManagerService.requestMagicLink(email, redirectTo);
            setSent(true);
        } catch (err) {
            toast.error(err.message || 'Failed to send magic link');
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4">
            <Toaster position="top-right" />

            <div className="w-full max-w-md">
                <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                    {/* Brand */}
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-tr from-emerald-400 to-cyan-500 shadow-lg mb-4">
                            <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                        </div>
                        <h1 className="text-2xl font-black text-gray-900 tracking-tight">Team Manager Sign In</h1>
                        <p className="text-sm text-gray-500 mt-1">Get a magic link sent to your email</p>
                    </div>

                    {!sent ? (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email address</label>
                                <input
                                    type="email"
                                    required
                                    autoFocus
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
                                    placeholder="manager@team.org"
                                    disabled={submitting}
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={submitting || !email.trim()}
                                className="w-full px-4 py-2.5 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {submitting ? 'Sending...' : 'Send magic link'}
                            </button>
                            <p className="text-xs text-gray-500 text-center pt-2">
                                Only registered team manager emails can access the roster.
                            </p>
                        </form>
                    ) : (
                        <div className="text-center space-y-4">
                            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100">
                                <svg className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-gray-900">Check your email</h2>
                                <p className="text-sm text-gray-500 mt-1">
                                    We sent a magic link to <span className="font-medium text-gray-700">{email}</span>.
                                    Click it to sign in.
                                </p>
                            </div>
                            <button
                                onClick={() => { setSent(false); setEmail(''); }}
                                className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                            >
                                Use a different email
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TeamLogin;
